import * as R from 'ramda';
import { HEX_ELEVATION_DOMAIN, HEX_COLORS } from './config';

// Get the LonLat position of a session
export const getPosition = session => [session.geo_point.lon, session.geo_point.lat];

// Get the duration in seconds of a session
export const getDuration = session => parseInt(session.duration);

// Predicate to test if a session key is a valid metric
const isMetricKey = (value, key) => key.endsWith('_score');

// Pipeline to calculate the overall score for a session
export const calculateOverallScore = R.compose(
  R.mean,
  R.filter(v => v !== null),
  R.values,
  R.pickBy(isMetricKey)
);

// Map a score to a color array
export const scoreToColor = score => {
  if (score < 40) {
    return HEX_COLORS[0];
  } else if (score > 70) {
    return HEX_COLORS[2];
  } else {
    return HEX_COLORS[1];
  }
};

export const getColor = R.compose(
  scoreToColor,
  calculateOverallScore
);

// Pipeline to calculate the color for a set of sessions
export const calculateColor = R.compose(
  R.head,
  R.sort((a, b) => a - b),
  R.map(calculateOverallScore)
);

export const calculateSingleElevation = R.compose(
  R.apply(R.clamp, HEX_ELEVATION_DOMAIN),
  session =>
    // if session is bad, force max elevation
    calculateOverallScore(session) < 40
      ? HEX_ELEVATION_DOMAIN[1]
      : getDuration(session)
);

// Pipeline to calculate the elevation for a set of sessions
// NOTE: elevation needs clamping within domain,
//       any values outside domain won't be displayed
export const calculateElevation = R.compose(
  calculateSingleElevation,
  R.head // only use first session in hex bin
);

// Convert RGB array to hex color
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

// Generate cluster icon SVG as data URL
export const createClusterIconSvg = (color, size = 99) => {
  const [r, g, b] = color;
  const hexColor = rgbToHex(r, g, b);
  const filterId = `filter0_d_${r}_${g}_${b}_${size}`;
  
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 99 99" fill="none">
      <circle opacity="0.3" cx="49.0809" cy="49.0809" r="49.0809" fill="${hexColor}"/>
      <circle opacity="0.5" cx="49.081" cy="49.0815" r="40.394" fill="${hexColor}"/>
      <g filter="url(#${filterId})">
        <circle cx="49.0809" cy="49.0821" r="31.7071" fill="${hexColor}"/>
      </g>
      <defs>
        <filter id="${filterId}" x="9.37378" y="9.375" width="79.4143" height="79.4141" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset/>
          <feGaussianBlur stdDeviation="4"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_164_77"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_164_77" result="shape"/>
        </filter>
      </defs>
    </svg>
  `.trim();
  
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Generate expanded marker icon SVG as data URL
export const createExpandedMarkerIconSvg = (color, size = 74, isSelected = false) => {
  const [r, g, b] = color;
  const hexColor = rgbToHex(r, g, b);
  const filterId = `filter0_d_expanded_${r}_${g}_${b}_${size}_${isSelected}`;
  const strokeColor = isSelected ? '#FFFFFF' : hexColor;
  
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 74 74" fill="none">
      <g filter="url(#${filterId})">
        <circle cx="37" cy="33" r="25.5" fill="black" fill-opacity="0.5" stroke="${strokeColor}" stroke-width="3"/>
        <circle cx="37" cy="33" r="19" fill="${hexColor}"/>
      </g>
      <defs>
        <filter id="${filterId}" x="0" y="0" width="74" height="74" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dy="4"/>
          <feGaussianBlur stdDeviation="5"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_164_280"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_164_280" result="shape"/>
        </filter>
      </defs>
    </svg>
  `.trim();
  
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
